import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Save, Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminFAQs = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [faqs, setFaqs] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchFAQs();
  }, [navigate]);

  const fetchFAQs = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/faqs`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFaqs(data);
      }
    } catch (error) {
      toast.error('Failed to load FAQs');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateFAQ = (index, field, value) => {
    const updated = [...faqs];
    updated[index] = { ...updated[index], [field]: value };
    setFaqs(updated);
  };

  const handleAddFAQ = () => {
    const newId = faqs.length > 0 ? Math.max(...faqs.map(f => f.id)) + 1 : 1;
    setFaqs([...faqs, {
      id: newId,
      question: '',
      answer: ''
    }]);
  };

  const handleDeleteFAQ = async (id, index) => {
    if (!window.confirm('Are you sure you want to delete this FAQ?')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await fetch(`${BACKEND_URL}/api/admin/faqs/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const updated = [...faqs];
      updated.splice(index, 1);
      setFaqs(updated);
      toast.success('FAQ deleted successfully!');
    } catch (error) {
      toast.error('Failed to delete FAQ');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      
      for (const faq of faqs) {
        if (faq.question && faq.answer) {
          await fetch(`${BACKEND_URL}/api/admin/faqs/${faq.id}`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(faq)
          });
        }
      }
      
      toast.success('FAQs updated successfully!');
      fetchFAQs();
    } catch (error) {
      toast.error('Failed to update FAQs');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="bg-gradient-to-r from-cyan-600 to-emerald-600 text-white p-6 shadow-lg">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard">
              <Button variant="outline" size="icon" className="text-white border-white hover:bg-white/10">
                <ArrowLeft className="w-4 h-4" />
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold">FAQs Management</h1>
              <p className="text-cyan-100">Manage frequently asked questions</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleAddFAQ} variant="outline" className="text-white border-white hover:bg-white/10">
              <Plus className="w-4 h-4 mr-2" />
              Add FAQ
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-white text-cyan-600 hover:bg-cyan-50">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto p-6 space-y-6">
        {faqs.map((faq, index) => (
          <Card key={faq.id}>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>FAQ #{faq.id}</CardTitle>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => handleDeleteFAQ(faq.id, index)}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Question</Label>
                <Input
                  value={faq.question}
                  onChange={(e) => handleUpdateFAQ(index, 'question', e.target.value)}
                  placeholder="Enter the question"
                />
              </div>
              <div>
                <Label>Answer</Label>
                <Textarea
                  value={faq.answer}
                  onChange={(e) => handleUpdateFAQ(index, 'answer', e.target.value)}
                  placeholder="Enter the answer"
                  rows={4}
                />
              </div>
            </CardContent>
          </Card>
        ))}
        
        {faqs.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 mb-4">No FAQs yet</p>
            <Button onClick={handleAddFAQ} className="bg-cyan-600 hover:bg-cyan-700">
              <Plus className="w-4 h-4 mr-2" />
              Add First FAQ
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminFAQs;
